import DesignTokens from '@tableau/design-tokens';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { Formik, FormikProps } from 'formik';
import React, { useState } from 'react';

import { Messages } from '~src/codegen/Localize';
import { FormikTextBox } from '~src/commonInfra/components/FormikField';
import { UpdateStringServerSettingNames } from '~src/settings/ServerApi/requests/serverSettings/UpdateStringServerSettingRequest';
import { ServerSettingsInstance } from '~src/settings/services/ServerSettings';
import { applyColor }from '~src/style/ColorService';
import { textFieldWidget } from '~src/userSettings/styles/EmailAndDisplayNameFormStyles';

export interface ExploreInTableauDialogProps {
  readonly close: (result: any) => void;
  onServerAdded?: (serverId: string) => void;
}

export interface ExploreInTableauDialogState {
  serverId: string;
  serverNameTaken?: boolean; // to be implemented in @W-18520797
}

const validServerId = /[^a-zA-Z0-9]|.{51,}|\s/;

export function validateServerId(value: string): string {
  if (!value || validServerId.test(value)) {
    return Messages.exploreInTableauInvalidServerId();
  }
  return '';
}

export const ExploreInTableauDialog: React.FC<ExploreInTableauDialogProps> = ({ close, onServerAdded }) => {
  const [state] = useState<ExploreInTableauDialogState>({
    serverId: '',
    serverNameTaken: false,
  });

  const handleSubmit = async (client: ExploreInTableauDialogState) => {
    await Promise.all([
      ServerSettingsInstance.updateString(
        UpdateStringServerSettingNames.cloudIntegrationTableauServerId,
        client.serverId),
      ServerSettingsInstance.updateString(
        UpdateStringServerSettingNames.cloudIntegrationInstantAnalyticsApiKey,
        '<INSERT-API-KEY>', // TODO W-18520797: Connect server admin front end to instant analytics API
        true) // encrypt the API key
    ]);

    if (onServerAdded) {
      onServerAdded(client.serverId);
    }

    close(true);
  };

  const getConnectionTypeDropdownSection = (formik: FormikProps<ExploreInTableauDialogState>): JSX.Element => {
    // to implement in @W-18409004
    return <></>;
  };

  const getClientIdSection = (): JSX.Element => {
    return (
      <tr>
        <td className='tb-text-input-label-cell tb-wide-overflow'>
          <label htmlFor='serverId'>{Messages.exploreInTableauServerIdLabel()}</label>
        </td>
        <td className='tb-settings-control tb-input-wide'>
          <FormikTextBox
            name='serverId'
            testId='eit-server-setting-dialog-server-id-field'
            label=''
            validate={validateServerId}
            className={textFieldWidget}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  };

  const initialValues = {
    serverId: '',
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values: ExploreInTableauDialogState) => {
        handleSubmit(values);
      }}
    >
      {(formik: FormikProps<ExploreInTableauDialogState>) => (
        <form onSubmit={formik.handleSubmit} data-tb-test-id='eit-server-setting-dialog'>
          <table className='tb-form-table'>
            <tbody>
              {getConnectionTypeDropdownSection(formik)}
              {getClientIdSection()}
            </tbody>
          </table>
          <div className='tb-dialog-actions'>
            {state.serverNameTaken !== undefined && state.serverNameTaken && (
              <div className='tb-dialog-message' style={{ color: applyColor(DesignTokens.color.font.default.error, 'red') }}>
                {Messages.exploreInTableauServerIdTaken()}
              </div>
            )}
            <ButtonWidget buttonType={ButtonType.Primary}
              testId='eit-server-setting-dialog-add-server-button'
              className='tb-margin-left-wide'
              disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
              onFocus={() => true}
              type='submit'>{Messages.add()}
            </ButtonWidget>
          </div>
        </form>
      )}
    </Formik>
  );
};
